import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { StagedSourceBadge } from '../StagedSourceBadge';
import { InlineMessage } from '../ui/InlineMessage';
import { StatusChip } from '../ui/StatusChip';
import {
  EMPTY_LAUNCH_VALUE,
  fieldProvenanceChip,
  formatLaunchDateValue,
  inferLaunchPrecision,
  type DatedFieldProvenanceValue,
  type LaunchDatePrecision,
} from './launchDateDisplay';

/**
 * Admin editor for a product's launch / availability dates, shared by the staged
 * product detail (MOT-647). The card's own Save still writes immediately. While
 * the panel is in edit mode, a dirty draft is also included in the panel's main
 * Save. Dates accept YYYY-MM-DD or YYYY-MM (month precision); empty clears that slot.
 */

export type { LaunchDatePrecision } from './launchDateDisplay';

export interface RegionalLaunchDateValue {
  date: string;
  precision?: LaunchDatePrecision;
  note?: string;
  dateProvenance?: DatedFieldProvenanceValue;
  precisionProvenance?: DatedFieldProvenanceValue;
  noteProvenance?: DatedFieldProvenanceValue;
}

export interface ProductLaunchDatesValue {
  /** Legacy pre-release rows stored `true` here, which is not a date. */
  announced?: string | boolean;
  announcedProvenance?: DatedFieldProvenanceValue;
  announcedNote?: string;
  announcedNoteProvenance?: DatedFieldProvenanceValue;
  us?: RegionalLaunchDateValue;
  canada?: RegionalLaunchDateValue;
  global?: RegionalLaunchDateValue;
  source?: 'gemini_grounded' | 'manual' | 'scraped' | string;
  checkedAt?: string | Date;
  confidence?: number;
}

export interface ProductLaunchDatesInput {
  announced: string | null;
  announcedNote: string | null;
  us: RegionalLaunchDateValue | null;
  canada: RegionalLaunchDateValue | null;
  global: RegionalLaunchDateValue | null;
}

interface Props {
  /** Staged/canonical product id — remount/reset when the dialog switches products. */
  productId?: string;
  launchDates: ProductLaunchDatesValue | null | undefined;
  onSave: (input: ProductLaunchDatesInput | null) => Promise<void>;
  /** Reports the unsaved edit so the panel's main Save can persist it too. */
  onPendingChange?: (pending: ProductLaunchDatesInput | null | undefined) => void;
  /** True while a dirty date fails validation, so the panel Save can stay blocked. */
  onInvalidChange?: (invalid: boolean) => void;
  /** When false, show saved values read-only (matches the panel edit toggle). */
  editMode?: boolean;
  disabled?: boolean;
}

const PRECISION_OPTIONS: LaunchDatePrecision[] = ['exact', 'month', 'quarter', 'year'];

const dayOf = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) return '';
  // Keep YYYY-MM for month-precision rows; trim time from ISO datetimes.
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  return trimmed;
};

const inferPrecision = (date: string, prior?: LaunchDatePrecision): LaunchDatePrecision => {
  if (prior) return prior;
  if (/^\d{4}-\d{2}$/.test(date)) return 'month';
  if (/^\d{4}$/.test(date)) return 'year';
  return 'exact';
};

const regionEqual = (
  a: { date: string; precision: LaunchDatePrecision; note: string },
  prior?: RegionalLaunchDateValue,
): boolean => {
  const priorDate = dayOf(prior?.date);
  const priorNote = prior?.note || '';
  const priorPrecision = prior?.precision || (priorDate ? inferPrecision(priorDate) : 'exact');
  return a.date === priorDate && a.note === priorNote && a.precision === priorPrecision;
};

const datePatternOk = (value: string): boolean =>
  !value || /^\d{4}(-\d{2}(-\d{2})?)?$/.test(value);

const ReadOnlyRow: React.FC<{
  label: string;
  date?: string | boolean | null;
  precision?: LaunchDatePrecision;
  note?: string | null;
  dateProvenance?: DatedFieldProvenanceValue | null;
  precisionProvenance?: DatedFieldProvenanceValue | null;
  noteProvenance?: DatedFieldProvenanceValue | null;
  cardSource?: string | null;
  cardConfidence?: number | null;
}> = ({ label, date, precision, note, dateProvenance, precisionProvenance, noteProvenance, cardSource, cardConfidence }) => {
  const dateText = typeof date === 'string' ? date : '';
  const formatted = dateText ? formatLaunchDateValue(dateText, precision || inferLaunchPrecision(dateText)) : '';
  const dateChip = formatted ? fieldProvenanceChip(dateProvenance, cardSource, cardConfidence) : null;
  const precisionChip = formatted && precision ? fieldProvenanceChip(precisionProvenance, cardSource, cardConfidence) : null;
  const noteChip = note ? fieldProvenanceChip(noteProvenance, cardSource, cardConfidence) : null;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 160px) minmax(0, 1fr)' },
        gap: 1,
        alignItems: 'start',
        py: 0.75,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, pt: 0.25 }}>
        {label}
      </Typography>
      <Stack spacing={0.35}>
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color={formatted ? 'text.primary' : 'text.disabled'}>
            {formatted || EMPTY_LAUNCH_VALUE}
          </Typography>
          {dateChip ? <StagedSourceBadge source={dateChip} label={dateChip.detail?.split(' · ')[0]} /> : null}
          {formatted && precision ? (
            <Typography variant="caption" color="text.secondary">
              {precision}
            </Typography>
          ) : null}
          {precisionChip ? <StagedSourceBadge source={precisionChip} label={precisionChip.detail?.split(' · ')[0]} /> : null}
        </Stack>
        {note ? (
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {note}
            </Typography>
            {noteChip ? <StagedSourceBadge source={noteChip} label={noteChip.detail?.split(' · ')[0]} /> : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

export const ProductLaunchDatesEditor: React.FC<Props> = ({
  productId,
  launchDates,
  onSave,
  onPendingChange,
  onInvalidChange,
  editMode = false,
  disabled = false,
}) => {
  const [announced, setAnnounced] = useState(dayOf(launchDates?.announced));
  const [announcedNote, setAnnouncedNote] = useState(launchDates?.announcedNote || '');
  const [usDate, setUsDate] = useState(dayOf(launchDates?.us?.date));
  const [usPrecision, setUsPrecision] = useState<LaunchDatePrecision>(
    launchDates?.us?.precision || inferPrecision(dayOf(launchDates?.us?.date)),
  );
  const [usNote, setUsNote] = useState(launchDates?.us?.note || '');
  const [canadaDate, setCanadaDate] = useState(dayOf(launchDates?.canada?.date));
  const [canadaPrecision, setCanadaPrecision] = useState<LaunchDatePrecision>(
    launchDates?.canada?.precision || inferPrecision(dayOf(launchDates?.canada?.date)),
  );
  const [canadaNote, setCanadaNote] = useState(launchDates?.canada?.note || '');
  const [globalDate, setGlobalDate] = useState(dayOf(launchDates?.global?.date));
  const [globalPrecision, setGlobalPrecision] = useState<LaunchDatePrecision>(
    launchDates?.global?.precision || inferPrecision(dayOf(launchDates?.global?.date)),
  );
  const [globalNote, setGlobalNote] = useState(launchDates?.global?.note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAnnounced(dayOf(launchDates?.announced));
    setAnnouncedNote(launchDates?.announcedNote || '');
    setUsDate(dayOf(launchDates?.us?.date));
    setUsPrecision(launchDates?.us?.precision || inferPrecision(dayOf(launchDates?.us?.date)));
    setUsNote(launchDates?.us?.note || '');
    setCanadaDate(dayOf(launchDates?.canada?.date));
    setCanadaPrecision(
      launchDates?.canada?.precision || inferPrecision(dayOf(launchDates?.canada?.date)),
    );
    setCanadaNote(launchDates?.canada?.note || '');
    setGlobalDate(dayOf(launchDates?.global?.date));
    setGlobalPrecision(
      launchDates?.global?.precision || inferPrecision(dayOf(launchDates?.global?.date)),
    );
    setGlobalNote(launchDates?.global?.note || '');
    setError(null);
    // productId: reset draft when “Open duplicate” swaps the detail product
    // without unmounting (identical empty launchDates would otherwise stick).
  }, [
    productId,
    launchDates?.announced,
    launchDates?.announcedNote,
    launchDates?.us?.date,
    launchDates?.us?.precision,
    launchDates?.us?.note,
    launchDates?.canada?.date,
    launchDates?.canada?.precision,
    launchDates?.canada?.note,
    launchDates?.global?.date,
    launchDates?.global?.precision,
    launchDates?.global?.note,
    editMode,
  ]);

  const datesInvalid =
    !datePatternOk(announced) ||
    !datePatternOk(usDate) ||
    !datePatternOk(canadaDate) ||
    !datePatternOk(globalDate);

  const dirty =
    announced !== dayOf(launchDates?.announced) ||
    announcedNote !== (launchDates?.announcedNote || '') ||
    !regionEqual({ date: usDate, precision: usPrecision, note: usNote }, launchDates?.us) ||
    !regionEqual(
      { date: canadaDate, precision: canadaPrecision, note: canadaNote },
      launchDates?.canada,
    ) ||
    !regionEqual(
      { date: globalDate, precision: globalPrecision, note: globalNote },
      launchDates?.global,
    );

  const hasAny =
    !!announced ||
    !!announcedNote ||
    !!usDate ||
    !!usNote ||
    !!canadaDate ||
    !!canadaNote ||
    !!globalDate ||
    !!globalNote;

  const buildInput = (): ProductLaunchDatesInput => {
    const region = (
      date: string,
      precision: LaunchDatePrecision,
      note: string,
    ): RegionalLaunchDateValue | null => {
      const d = date.trim();
      if (!d) return null;
      const n = note.trim();
      return {
        date: d,
        precision: inferPrecision(d, precision),
        ...(n ? { note: n } : {}),
      };
    };
    return {
      announced: announced.trim() || null,
      announcedNote: announcedNote.trim() || null,
      us: region(usDate, usPrecision, usNote),
      canada: region(canadaDate, canadaPrecision, canadaNote),
      global: region(globalDate, globalPrecision, globalNote),
    };
  };

  const run = async (input: ProductLaunchDatesInput | null) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save launch dates');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!onPendingChange) return;
    if (!editMode || !dirty) {
      onPendingChange(undefined);
      return;
    }
    if (datesInvalid) {
      onPendingChange(undefined);
      return;
    }
    if (!hasAny) {
      onPendingChange(null);
      return;
    }
    onPendingChange(buildInput());
    // The draft fields are the dependencies; buildInput reads them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editMode,
    dirty,
    hasAny,
    datesInvalid,
    onPendingChange,
    announced,
    announcedNote,
    usDate,
    usPrecision,
    usNote,
    canadaDate,
    canadaPrecision,
    canadaNote,
    globalDate,
    globalPrecision,
    globalNote,
  ]);

  useEffect(() => {
    onInvalidChange?.(Boolean(editMode && dirty && datesInvalid));
  }, [onInvalidChange, editMode, dirty, datesInvalid]);

  const cardChip = fieldProvenanceChip(undefined, launchDates?.source, launchDates?.confidence);

  const checkedLabel = (() => {
    const raw = launchDates?.checkedAt;
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw.toLocaleString();
    return '';
  })();

  const header = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {launchDates ? (
        <>
          <StatusChip size="small" tone="info" label="Launch dates" />
          {cardChip ? <StagedSourceBadge source={cardChip} label={cardChip.detail?.split(' · ')[0]} /> : null}
          <Typography variant="caption" color="text.secondary">
            {checkedLabel ? `Checked ${checkedLabel}` : ''}
          </Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No launch dates recorded. Set announced and regional availability so stores and the
          pre-order button can follow them.
        </Typography>
      )}
    </Stack>
  );

  if (!editMode) {
    return (
      <Stack spacing={1}>
        {header}
        <ReadOnlyRow
          label="Announced"
          date={launchDates?.announced}
          note={launchDates?.announcedNote}
          dateProvenance={launchDates?.announcedProvenance}
          noteProvenance={launchDates?.announcedNoteProvenance}
          cardSource={launchDates?.source}
          cardConfidence={launchDates?.confidence}
        />
        <ReadOnlyRow
          label="US availability"
          date={launchDates?.us?.date}
          precision={launchDates?.us?.precision}
          note={launchDates?.us?.note}
          dateProvenance={launchDates?.us?.dateProvenance}
          precisionProvenance={launchDates?.us?.precisionProvenance}
          noteProvenance={launchDates?.us?.noteProvenance}
          cardSource={launchDates?.source}
          cardConfidence={launchDates?.confidence}
        />
        <ReadOnlyRow
          label="Canada availability"
          date={launchDates?.canada?.date}
          precision={launchDates?.canada?.precision}
          note={launchDates?.canada?.note}
          dateProvenance={launchDates?.canada?.dateProvenance}
          precisionProvenance={launchDates?.canada?.precisionProvenance}
          noteProvenance={launchDates?.canada?.noteProvenance}
          cardSource={launchDates?.source}
          cardConfidence={launchDates?.confidence}
        />
        <ReadOnlyRow
          label="Global availability"
          date={launchDates?.global?.date}
          precision={launchDates?.global?.precision}
          note={launchDates?.global?.note}
          dateProvenance={launchDates?.global?.dateProvenance}
          precisionProvenance={launchDates?.global?.precisionProvenance}
          noteProvenance={launchDates?.global?.noteProvenance}
          cardSource={launchDates?.source}
          cardConfidence={launchDates?.confidence}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      {header}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          size="small"
          label="Announced"
          value={announced}
          onChange={(e) => setAnnounced(e.target.value)}
          disabled={disabled || saving}
          error={!datePatternOk(announced)}
          helperText="YYYY-MM-DD or YYYY-MM"
          placeholder="YYYY-MM-DD"
          sx={{ flex: 1 }}
          slotProps={{ htmlInput: { 'aria-label': 'Announced date' } }}
        />
        <TextField
          size="small"
          label="Announced note"
          value={announcedNote}
          onChange={(e) => setAnnouncedNote(e.target.value)}
          disabled={disabled || saving}
          placeholder="e.g. Official product launch announcement"
          sx={{ flex: 2 }}
          slotProps={{ htmlInput: { 'aria-label': 'Announced note' } }}
        />
      </Stack>

      {(
        [
          {
            key: 'us',
            label: 'US availability',
            date: usDate,
            setDate: setUsDate,
            precision: usPrecision,
            setPrecision: setUsPrecision,
            note: usNote,
            setNote: setUsNote,
          },
          {
            key: 'canada',
            label: 'Canada availability',
            date: canadaDate,
            setDate: setCanadaDate,
            precision: canadaPrecision,
            setPrecision: setCanadaPrecision,
            note: canadaNote,
            setNote: setCanadaNote,
          },
          {
            key: 'global',
            label: 'Global availability',
            date: globalDate,
            setDate: setGlobalDate,
            precision: globalPrecision,
            setPrecision: setGlobalPrecision,
            note: globalNote,
            setNote: setGlobalNote,
          },
        ] as const
      ).map((row) => (
        <Stack key={row.key} direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            label={row.label}
            value={row.date}
            onChange={(e) => row.setDate(e.target.value)}
            disabled={disabled || saving}
            error={!datePatternOk(row.date)}
            helperText="YYYY-MM-DD or YYYY-MM"
            placeholder="YYYY-MM-DD"
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { 'aria-label': row.label } }}
          />
          <TextField
            select
            size="small"
            label="Precision"
            value={row.precision}
            onChange={(e) => row.setPrecision(e.target.value as LaunchDatePrecision)}
            disabled={disabled || saving || !row.date}
            sx={{ minWidth: 140 }}
            slotProps={{ htmlInput: { 'aria-label': `${row.label} precision` } }}
          >
            {PRECISION_OPTIONS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Note"
            value={row.note}
            onChange={(e) => row.setNote(e.target.value)}
            disabled={disabled || saving}
            placeholder="e.g. mid-November 2026"
            sx={{ flex: 1.5 }}
            slotProps={{ htmlInput: { 'aria-label': `${row.label} note` } }}
          />
        </Stack>
      ))}

      {datesInvalid && (
        <InlineMessage severity="warning">
          Fix the date before Save changes. The other edits in this card stay here until it is a year, month, or day.
        </InlineMessage>
      )}
      {error && <InlineMessage severity="error">{error}</InlineMessage>}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="contained"
          disabled={disabled || saving || !dirty || datesInvalid || !hasAny}
          onClick={() => run(buildInput())}
        >
          {saving ? 'Saving…' : 'Save launch dates'}
        </Button>
        {launchDates && (
          <Button size="small" color="inherit" disabled={disabled || saving} onClick={() => run(null)}>
            Clear launch dates
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default ProductLaunchDatesEditor;
